import React, { useCallback } from 'react'

import { rem } from 'polished'
import styled from 'styled-components'

import { addToCheckoutCart, initialiseCheckout } from 'actions/CheckoutActions'
import { generateBNBLCheckoutItem } from 'lib/checkout/accommodation/cart'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useAppDispatch } from 'hooks/reduxHooks'
import FormatCurrency from 'components/Common/FormatCurrency'
import InlineGroup from 'components/Common/InlineGroup'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import TextButton from 'components/Luxkit/Button/TextButton'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/getValueOffPercent'
import Pill from 'components/Luxkit/Pill'
import { getBnblPackageViewsTotalPrice } from 'selectors/offerPageSelectors'
import { BnblTotalPakcagesPrices } from 'components/OfferPage/BnblModal/utils'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import * as Analytics from 'analytics/analytics'
import { addToCartEvent } from 'analytics/optimizely/events'

const StyledTotalCaption = styled(Caption)`
  margin-left: ${rem(4)};
`

const StyledInlineGroup = styled(InlineGroup)`
  width: 100%;
`

const StyledPill = styled(Pill)`
  margin-left: ${rem(8)};
`

interface MappedProps {
  bnblModalPackages: App.BnblModal[];
  totalRoomsPrice: BnblTotalPakcagesPrices;
}

interface Props {
  offer: App.Offer;
  isGift: boolean;
}

function BnblPackagePricing(props: Props & MappedProps) {
  const { bnblModalPackages, totalRoomsPrice, offer, isGift } = props
  const dispatch = useAppDispatch()

  const { hotelValue, hotelPrice, priceFrom, shouldDisplayValue } = totalRoomsPrice

  const shouldShowValue = hotelValue > 0 && hotelValue > priceFrom && shouldDisplayValue
  const valueOffPercent = getValueOffPercent(hotelValue, hotelPrice)
  const shouldShowRate = valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const onBuyNow = useCallback(() => {
    const checkoutItems = bnblModalPackages.reduce<Array<App.Checkout.BNBLLEHotelItem>>((array, modalPackage) => {
      const items = generateBNBLCheckoutItem(offer, modalPackage.packageView.package, modalPackage.numberOfRooms)
      return [...array, ...items]
    }, [])
    Analytics.track('add-to-cart', addToCartEvent())
    if (isGift) {
      // TODO: isGift cannot work with multiple cart items
      dispatch(initialiseCheckout(checkoutItems, { isGift }))
    } else {
      dispatch(addToCheckoutCart(checkoutItems))
    }
    dispatch(navigateToCheckout())
  }, [isGift, offer, bnblModalPackages, dispatch])

  return (
    <StyledInlineGroup horizontal="between" vertical="center">
      <div>
        <Caption variant="medium" >From</Caption>
        <InlineGroup vertical="center">
          <Heading variant="heading6">
            <FormatCurrency value={priceFrom} format="preciseDollar" />
          </Heading>
          <StyledTotalCaption variant="medium">total</StyledTotalCaption>
        </InlineGroup>
        {shouldShowValue &&
          <Caption variant="medium" weight="bold">
            Valued up to&nbsp;
            <FormatCurrency value={hotelValue} format="preciseDollar" />
            {shouldShowRate &&
              <StyledPill kind="primary" variant="success">-{valueOffPercent}%</StyledPill>
            }
          </Caption>
        }
        <Caption colour="neutral-three" variant="medium">Includes taxes and fees</Caption>
      </div>
      <div>
        <TextButton data-testid="bnbl-buy-btn" kind="primary" onClick={onBuyNow} size="large">Buy now</TextButton>
      </div>
    </StyledInlineGroup>
  )
}

export default withOfferPageAndReduxState((offerPageState, appState: App.State, ownProps: Props): MappedProps => {
  return {
    totalRoomsPrice: getBnblPackageViewsTotalPrice(offerPageState, appState),
    bnblModalPackages: appState.offer.bnblModal[ownProps.offer.id],
  }
})(BnblPackagePricing)
